import { useCallback, useEffect, useState } from 'react';
import { FlatList, StyleSheet, Text, View } from 'react-native';

import { URGENT_HANDOFF_CARD_WIDTH, UrgentHandoffCard } from '@/components/molecules/ai-baton';
import { useTranslation } from '@/i18n';
import { type HandoffItem } from '@/models/handoffs';
import { spacing, unisonColors } from '@/theme';

interface UrgentHandoffCarouselProps {
  readonly handoffs: readonly HandoffItem[];
  readonly updatingHandoffId: string | null;
  readonly onClaim: (handoffId: string) => void;
  readonly onPress?: (handoffId: string) => void;
}

const keyExtractor = (item: HandoffItem) => item.id;

const ItemSeparator = () => <View style={styles.separator} />;

export const UrgentHandoffCarousel = ({
  handoffs,
  updatingHandoffId,
  onClaim,
  onPress,
}: UrgentHandoffCarouselProps) => {
  const { t } = useTranslation();
  const [tallestCardHeight, setTallestCardHeight] = useState(0);

  const handoffIds = handoffs.map((handoff) => handoff.id).join(',');

  useEffect(() => {
    setTallestCardHeight(0);
  }, [handoffIds]);

  const handleCardMeasure = useCallback((height: number) => {
    setTallestCardHeight((current) => (height > current ? height : current));
  }, []);

  const renderItem = useCallback(
    ({ item }: { item: HandoffItem }) => (
      <UrgentHandoffCard
        isUpdating={updatingHandoffId === item.id}
        item={item}
        minHeight={tallestCardHeight}
        onClaim={onClaim}
        onMeasure={handleCardMeasure}
        onPress={onPress}
      />
    ),
    [handleCardMeasure, onClaim, onPress, tallestCardHeight, updatingHandoffId],
  );

  if (handoffs.length === 0) {
    return null;
  }

  return (
    <View style={styles.container} testID="urgent-handoff-carousel">
      <Text style={styles.title}>{t('aiTasks.urgentSectionTitle')}</Text>
      <FlatList<HandoffItem>
        data={handoffs}
        horizontal
        ItemSeparatorComponent={ItemSeparator}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        showsHorizontalScrollIndicator={false}
        snapToAlignment="start"
        snapToInterval={URGENT_HANDOFF_CARD_WIDTH + spacing[4]}
        decelerationRate="fast"
        // Headroom so the unread dot's overhang beyond a card's top-left
        // corner (4px up, 3px left) is not clipped by the scroll viewport edges.
        contentContainerStyle={styles.listContent}
        testID="urgent-handoff-carousel-list"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    rowGap: spacing[2],
    paddingBottom: spacing[2],
  },
  listContent: {
    paddingLeft: 4,
    paddingTop: 4,
  },
  title: {
    color: unisonColors.textNdPrimary,
    fontSize: 16,
    fontWeight: 'bold',
    lineHeight: 24,
  },
  separator: {
    width: spacing[4],
  },
});
